
/**
 * Utilitários para calcular tempo médio de resposta
 * 
 * O que faz:
 * - Calcula tempo médio de resposta baseado em mensagens de chat
 * - Aplica filtros de horário comercial e tipo de resposta
 * - Formata tempos para exibição
 * 
 * Como funciona:
 * 1. Identifica sequências de mensagem: lead → resposta do sistema/usuário
 * 2. Calcula o tempo entre o envio do lead e a primeira resposta
 * 3. Aplica filtros de horário comercial se solicitado
 * 4. Categoriza respostas por tipo (humano/IA)
 */

import { differenceInMinutes, isWithinInterval, parseISO, getHours, getMinutes, getDay } from 'date-fns';

export interface ChatMessage {
  id: string;
  lead_id: string;
  // Esses campos sao opcionais porque o dashboard usa apenas a sequencia temporal.
  // Motivo: permitir consultas mais leves sem obrigar o carregamento do texto da mensagem.
  clinica_id?: string;
  conteudo?: string;
  enviado_por: 'lead' | 'usuario' | 'ia';
  created_at: string;
}

export interface BusinessHours {
  // Horário de funcionamento durante a semana
  weekdayStart: string; // formato HH:mm
  weekdayEnd: string;
  // Configurações de final de semana
  saturdayActive: boolean;
  saturdayStart?: string;
  saturdayEnd?: string;
  sundayActive: boolean;
  sundayStart?: string;
  sundayEnd?: string;
}

export interface ResponseTimeFilters {
  // Tipos de resposta a considerar
  includeHuman: boolean;    // Respostas de usuários
  includeAI: boolean;       // Respostas da IA
  // Considerar apenas horário comercial
  businessHoursOnly: boolean;
}

interface ResponseTimeSample {
  tempo: number;
  isHuman: boolean;
  isAI: boolean;
  emHorarioComercial: boolean;
}

interface ResponseTimeSamples {
  respostas: ResponseTimeSample[];
}

/**
 * Percorre cada conversa uma única vez e separa as amostras para os quatro cards.
 * Motivo: o dashboard exibe geral, humano, IA e horário comercial na mesma carga;
 * ordenar e percorrer as mensagens quatro vezes fazia períodos longos travarem a UI.
 */
const coletarAmostrasTempoResposta = (
  messages: ChatMessage[],
  businessHours?: BusinessHours,
): ResponseTimeSamples => {
  const messagesByLead = messages.reduce((acc, message) => {
    if (!acc[message.lead_id]) acc[message.lead_id] = [];
    acc[message.lead_id].push(message);
    return acc;
  }, {} as Record<string, ChatMessage[]>);

  const samples: ResponseTimeSamples = { respostas: [] };

  Object.values(messagesByLead).forEach((leadMessages) => {
    // A cópia evita alterar a ordem do array recebido pelo componente chamador.
    const sortedMessages = [...leadMessages].sort((a, b) => {
      const difference = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      // O ID desempata timestamps iguais para reproduzir a mesma ordem do Supabase.
      return difference || a.id.localeCompare(b.id);
    });

    let i = 0;
    while (i < sortedMessages.length) {
      const currentMessage = sortedMessages[i];

      // Uma solicitação é a sequência de mensagens consecutivas enviadas pelo lead.
      if (currentMessage.enviado_por !== 'lead') {
        i += 1;
        continue;
      }

      let lastLeadTime = parseISO(currentMessage.created_at);
      let j = i + 1;
      while (j < sortedMessages.length && sortedMessages[j].enviado_por === 'lead') {
        lastLeadTime = parseISO(sortedMessages[j].created_at);
        j += 1;
      }

      if (j >= sortedMessages.length) {
        i = j;
        continue;
      }

      const responseMessage = sortedMessages[j];
      const responseTime = parseISO(responseMessage.created_at);
      const timeInMinutes = differenceInMinutes(responseTime, lastLeadTime);

      // Mantém a mesma regra do cálculo anterior: descarta tempos negativos e outliers acima de uma semana.
      if (timeInMinutes >= 0 && timeInMinutes <= 10080) {
        const isHumanResponse = responseMessage.enviado_por === 'usuario';
        const isAIResponse = responseMessage.enviado_por === 'ia';

        // As visões humano/IA mantêm respostas desconhecidas, como a função legada fazia.
        samples.respostas.push({
          tempo: timeInMinutes,
          isHuman: isHumanResponse,
          isAI: isAIResponse,
          emHorarioComercial: Boolean(businessHours && isWithinBusinessHours(responseTime, businessHours)),
        });
      }

      // Avança para depois da primeira resposta para preservar a semântica de sequência.
      i = j + 1;
    }
  });

  return samples;
};

const selecionarAmostras = (
  samples: ResponseTimeSamples,
  businessHours: BusinessHours | undefined,
  filters: ResponseTimeFilters,
) => samples.respostas.filter((sample) => {
  if (filters.businessHoursOnly && businessHours && !sample.emHorarioComercial) return false;
  if (!filters.includeHuman && sample.isHuman) return false;
  if (!filters.includeAI && sample.isAI) return false;
  return true;
});

const criarResultadoTempoResposta = (
  responseTimes: number[],
  humanResponseTimes: number[],
  aiResponseTimes: number[],
  businessHoursTimes: number[],
) => {
  const calculateAverage = (times: number[]) =>
    times.length > 0 ? times.reduce((sum, time) => sum + time, 0) / times.length : 0;

  const averageTime = calculateAverage(responseTimes);
  const averageHumanTime = calculateAverage(humanResponseTimes);
  const averageAITime = calculateAverage(aiResponseTimes);
  const averageBusinessTime = calculateAverage(businessHoursTimes);

  // Uma única redução calcula a distribuição para evitar quatro filters sobre o mesmo array.
  const distribution = responseTimes.reduce((acc, time) => {
    if (time <= 30) acc.ate30min += 1;
    else if (time <= 60) acc.de30mina1h += 1;
    else if (time <= 240) acc.de1ha4h += 1;
    else acc.acimaDe4h += 1;
    return acc;
  }, { ate30min: 0, de30mina1h: 0, de1ha4h: 0, acimaDe4h: 0 });

  return {
    tempoMedioMinutos: averageTime,
    tempoMedioFormatado: formatResponseTime(averageTime),
    classificacao: classifyResponseTime(averageTime),
    detalhes: {
      tempoMedioHumano: averageHumanTime,
      tempoMedioHumanoFormatado: formatResponseTime(averageHumanTime),
      tempoMedioIA: averageAITime,
      tempoMedioIAFormatado: formatResponseTime(averageAITime),
      tempoMedioComercial: averageBusinessTime,
      tempoMedioComercialFormatado: formatResponseTime(averageBusinessTime),
      distribuicao: distribution,
    },
    totalRespostas: responseTimes.length,
  };
};

/**
 * Formata tempo em minutos para exibição amigável
 */
export const formatResponseTime = (minutes: number): string => {
  if (minutes < 1) {
    return '< 1min';
  }
  
  if (minutes < 60) {
    return `${Math.round(minutes)}min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  
  if (hours < 24) {
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  }
  
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  
  if (remainingHours === 0) {
    return `${days}d`;
  }
  return `${days}d ${remainingHours}h`;
};

/**
 * Classifica o tempo de resposta em categorias de performance
 */
export const classifyResponseTime = (minutes: number): 'excelente' | 'bom' | 'regular' | 'ruim' => {
  if (minutes <= 60) return 'excelente';      // Até 1 hora
  if (minutes <= 240) return 'bom';           // Até 4 horas
  if (minutes <= 1440) return 'regular';      // Até 1 dia
  return 'ruim';                              // Mais de 1 dia
};

/**
 * Verifica se um momento está dentro do horário comercial
 */
export const isWithinBusinessHours = (date: Date, businessHours: BusinessHours): boolean => {
  const dayOfWeek = getDay(date); // 0 = domingo, 1 = segunda, etc.
  const currentTime = `${getHours(date).toString().padStart(2, '0')}:${getMinutes(date).toString().padStart(2, '0')}`;
  
  // Verificar finais de semana
  if (dayOfWeek === 6) { // Sábado
    if (!businessHours.saturdayActive) return false;
    if (!businessHours.saturdayStart || !businessHours.saturdayEnd) return false;
    return currentTime >= businessHours.saturdayStart && currentTime <= businessHours.saturdayEnd;
  }
  
  if (dayOfWeek === 0) { // Domingo
    if (!businessHours.sundayActive) return false;
    if (!businessHours.sundayStart || !businessHours.sundayEnd) return false;
    return currentTime >= businessHours.sundayStart && currentTime <= businessHours.sundayEnd;
  }
  
  // Dias úteis (segunda a sexta)
  return currentTime >= businessHours.weekdayStart && currentTime <= businessHours.weekdayEnd;
};

/**
 * Calcula o tempo médio de resposta baseado nas mensagens
 */
export const calculateResponseTime = (
  messages: ChatMessage[],
  businessHours?: BusinessHours,
  filters: ResponseTimeFilters = {
    includeHuman: true,
    includeAI: true,
    businessHoursOnly: false
  }
) => {
  const samples = coletarAmostrasTempoResposta(messages, businessHours);
  const selecionadas = selecionarAmostras(samples, businessHours, filters);
  const responseTimes = selecionadas.map((sample) => sample.tempo);
  const humanResponseTimes = selecionadas
    .filter((sample) => sample.isHuman)
    .map((sample) => sample.tempo);
  const aiResponseTimes = selecionadas
    .filter((sample) => sample.isAI)
    .map((sample) => sample.tempo);
  const businessHoursTimes = selecionadas
    .filter((sample) => sample.emHorarioComercial)
    .map((sample) => sample.tempo);

  return criarResultadoTempoResposta(responseTimes, humanResponseTimes, aiResponseTimes, businessHoursTimes);
};

/**
 * Versão em lote usada pelo dashboard: calcula os quatro recortes com uma única ordenação.
 */
export const calculateResponseTimeVariants = (messages: ChatMessage[], businessHours?: BusinessHours) => {
  const samples = coletarAmostrasTempoResposta(messages, businessHours);
  const criarResultadoSelecionado = (filters: ResponseTimeFilters) => {
    const selecionadas = selecionarAmostras(samples, businessHours, filters);
    return criarResultadoTempoResposta(
      selecionadas.map((sample) => sample.tempo),
      selecionadas.filter((sample) => sample.isHuman).map((sample) => sample.tempo),
      selecionadas.filter((sample) => sample.isAI).map((sample) => sample.tempo),
      selecionadas.filter((sample) => sample.emHorarioComercial).map((sample) => sample.tempo),
    );
  };

  return {
    geral: criarResultadoSelecionado({ includeHuman: true, includeAI: true, businessHoursOnly: false }),
    humano: criarResultadoSelecionado({ includeHuman: true, includeAI: false, businessHoursOnly: false }),
    ia: criarResultadoSelecionado({ includeHuman: false, includeAI: true, businessHoursOnly: false }),
    comercial: criarResultadoSelecionado({ includeHuman: true, includeAI: true, businessHoursOnly: true }),
  };
};
